import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, Check, ChevronLeft, ChevronRight, Copy, KeyRound, MoreVertical, Plus, Search, ShieldAlert, ShieldCheck, Trash2, X } from "lucide-react";
import QRCode from "qrcode";
import { googleAuthenticatorMigrationUris } from "../lib/authenticator-export";
import { parseOtpAuthUri, totp } from "../lib/totp";
import type { AuthenticatorRecord, SecretRecord } from "../types";

const AuthenticatorImport = lazy(() => import("./AuthenticatorImport").then((module) => ({ default: module.AuthenticatorImport })));

export type AuthenticatorInput = Pick<AuthenticatorRecord, "issuer" | "account" | "secret" | "digits" | "period" | "algorithm">;
export type AuthenticatorSource = AuthenticatorRecord | SecretRecord;

function detailsFor(record: AuthenticatorSource): AuthenticatorInput {
  if (record.kind === "authenticator") return record;
  return {
    issuer: record.title,
    account: record.username,
    secret: record.totpSecret,
    digits: 6,
    period: 30,
    algorithm: "SHA-1"
  };
}

const emptyDetails = (): AuthenticatorInput => ({ issuer: "", account: "", secret: "", digits: 6, period: 30, algorithm: "SHA-1" });

export function AuthenticatorApp({ records, clipboardSeconds, onCreate, onUpdate, onDelete }: {
  records: AuthenticatorSource[];
  clipboardSeconds: number;
  onCreate: (input: AuthenticatorInput) => Promise<void>;
  onUpdate: (record: AuthenticatorSource, input: AuthenticatorInput) => Promise<void>;
  onDelete: (record: AuthenticatorSource) => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [now, setNow] = useState(Date.now());
  const [codes, setCodes] = useState<Record<string, { code: string; remaining: number; error?: boolean }>>({});
  const [editing, setEditing] = useState<AuthenticatorSource | "new" | null>(null);
  const [draft, setDraft] = useState<AuthenticatorInput>(emptyDetails);
  const [setupValue, setSetupValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSelected, setExportSelected] = useState<Set<string>>(new Set());
  const [exportImages, setExportImages] = useState<string[]>([]);
  const [exportPage, setExportPage] = useState(0);
  const [exportError, setExportError] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(records.filter((record) => !record.deletedAt).map(async (record) => {
      const details = detailsFor(record);
      try {
        const value = await totp(details.secret, now, details);
        return [record.id, value] as const;
      } catch {
        return [record.id, { code: "Invalid", remaining: details.period, error: true }] as const;
      }
    })).then((values) => { if (!cancelled) setCodes(Object.fromEntries(values)); });
    return () => { cancelled = true; };
  }, [now, records]);

  const visible = useMemo(() => {
    const value = query.trim().toLocaleLowerCase();
    return records.filter((record) => {
      if (record.deletedAt) return false;
      const details = detailsFor(record);
      return !value || `${details.issuer} ${details.account}`.toLocaleLowerCase().includes(value);
    }).sort((left, right) => detailsFor(left).issuer.localeCompare(detailsFor(right).issuer));
  }, [query, records]);

  const openNew = () => {
    setDraft(emptyDetails());
    setSetupValue("");
    setError("");
    setEditing("new");
  };
  const openEdit = (record: AuthenticatorSource) => {
    const details = detailsFor(record);
    setDraft(details);
    setSetupValue(details.secret);
    setError("");
    setEditing(record);
  };
  const changeSetup = (value: string) => {
    setSetupValue(value);
    setError("");
    if (value.trim().toLowerCase().startsWith("otpauth://")) {
      try { setDraft(parseOtpAuthUri(value)); } catch (cause) { setError(cause instanceof Error ? cause.message : "Invalid setup link"); }
    } else {
      setDraft((current) => ({ ...current, secret: value.toUpperCase().replace(/[\s=-]/g, "") }));
    }
  };
  const submit = async () => {
    const next = { ...draft, issuer: draft.issuer.trim(), account: draft.account.trim(), secret: draft.secret.trim().toUpperCase().replace(/[\s=-]/g, "") };
    if (!next.issuer || !next.secret) { setError("Name and setup key are required."); return; }
    setBusy(true);
    setError("");
    try {
      await totp(next.secret, Date.now(), next);
      if (editing === "new") await onCreate(next);
      else if (editing) await onUpdate(editing, next);
      setEditing(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save this account");
    } finally { setBusy(false); }
  };
  const copyCode = async (id: string, code: string) => {
    if (!/^\d+$/.test(code)) return;
    await navigator.clipboard.writeText(code);
    setCopied(id);
    window.setTimeout(() => setCopied((current) => current === id ? null : current), 1_200);
    window.setTimeout(() => navigator.clipboard.writeText("").catch(() => undefined), clipboardSeconds * 1_000);
  };
  const remove = async (record: AuthenticatorSource) => {
    if (!window.confirm(`Remove the authenticator code for ${detailsFor(record).issuer}?`)) return;
    await onDelete(record);
    setEditing(null);
  };
  const openExport = () => {
    setExportSelected(new Set(records.filter((record) => !record.deletedAt && detailsFor(record).period === 30).map((record) => record.id)));
    setExportImages([]);
    setExportPage(0);
    setExportError("");
    setExportOpen(true);
  };
  const closeExport = () => {
    setExportOpen(false);
    setExportImages([]);
    setExportError("");
  };
  const createExport = async () => {
    setExportBusy(true);
    setExportError("");
    try {
      const selected = records.filter((record) => exportSelected.has(record.id)).map(detailsFor);
      const uris = googleAuthenticatorMigrationUris(selected);
      const images = await Promise.all(uris.map((uri) => QRCode.toDataURL(uri, { width: 320, margin: 2, errorCorrectionLevel: "M", color: { dark: "#111528", light: "#ffffff" } })));
      setExportImages(images);
      setExportPage(0);
    } catch (cause) {
      setExportError(cause instanceof Error ? cause.message : "Could not create transfer QR code");
    } finally { setExportBusy(false); }
  };

  return <div className="authenticator-app">
    <div className="authenticator-mobile">
      <header className="authenticator-header">
        <div className="authenticator-brand"><span><ShieldCheck size={22} /></span><div><strong>Authenticator</strong><small>Encrypted on this device</small></div></div>
        <div className="authenticator-header-actions"><button aria-label="Import accounts" onClick={() => setImportOpen(true)}><ArrowDownToLine size={19} /></button><button aria-label="Export accounts" disabled={!records.some((record) => !record.deletedAt)} onClick={openExport}><ArrowUpFromLine size={19} /></button><button aria-label="Add account" onClick={openNew}><Plus size={22} /></button></div>
      </header>
      <label className="authenticator-search"><Search size={17} /><input aria-label="Search authenticator accounts" placeholder="Search accounts" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
      <main className="authenticator-list">
        {visible.map((record) => {
          const details = detailsFor(record);
          const value = codes[record.id] ?? { code: "••••••", remaining: details.period };
          const circumference = 2 * Math.PI * 18;
          const progress = Math.max(0, Math.min(1, value.remaining / details.period));
          return <article className={`authenticator-card ${value.remaining <= 5 ? "expiring" : ""}`} key={record.id}>
            <div className="authenticator-card-head">
              <span className="authenticator-avatar">{details.issuer.slice(0, 1).toUpperCase() || <KeyRound size={17} />}</span>
              <span className="authenticator-account"><strong>{details.issuer}</strong><small>{details.account || "TOTP account"}</small></span>
              <button className="authenticator-more" aria-label={`Edit ${details.issuer}`} onClick={() => openEdit(record)}><MoreVertical size={19} /></button>
            </div>
            <button className="authenticator-code" aria-label={`Copy code for ${details.issuer}`} onClick={() => void copyCode(record.id, value.code)}>
              <span className="authenticator-code-value">
                <b className="authenticator-digits">{value.error ? value.code : `${value.code.slice(0, value.code.length / 2)} ${value.code.slice(value.code.length / 2)}`}</b>
                <small className={`authenticator-copy-cue ${copied === record.id ? "copied" : ""}`}>{copied === record.id ? <><Check size={12} />Copied</> : <><Copy size={12} />Tap to copy</>}</small>
              </span>
              <span className="authenticator-timer">
                <svg viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r="18" /><circle className="progress" cx="22" cy="22" r="18" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - progress)} /></svg>
                <small>{value.remaining}</small>
              </span>
            </button>
          </article>;
        })}
        {visible.length === 0 && <div className="authenticator-empty"><ShieldCheck size={48} /><strong>{query ? "No matching accounts" : "No authenticator accounts"}</strong><span>Add a setup key or paste an otpauth link to generate secure verification codes.</span><button onClick={openNew}><Plus size={16} />Add account</button></div>}
      </main>
      <button className="authenticator-fab" aria-label="Add authenticator account" onClick={openNew}><Plus size={25} /></button>
      <nav className="authenticator-nav"><span className="active"><ShieldCheck size={19} />Codes</span><span><KeyRound size={19} />Protected</span></nav>
      {editing && <div className="authenticator-sheet-backdrop" onPointerDown={(event) => { if (event.target === event.currentTarget) setEditing(null); }}>
        <section className="authenticator-sheet">
          <div className="authenticator-sheet-handle" />
          <header><div><strong>{editing === "new" ? "Add account" : "Edit account"}</strong><small>Paste the key provided by the service</small></div><button aria-label="Close" onClick={() => setEditing(null)}><X size={20} /></button></header>
          <label>Account name<input autoFocus value={draft.issuer} onChange={(event) => setDraft({ ...draft, issuer: event.target.value })} placeholder="GitHub" /></label>
          <label>Email or username<input value={draft.account} onChange={(event) => setDraft({ ...draft, account: event.target.value })} placeholder="you@example.com" /></label>
          <label>Setup key or otpauth link<textarea rows={3} value={setupValue} onChange={(event) => changeSetup(event.target.value)} placeholder="JBSW Y3DP EHPK 3PXP" spellCheck={false} /></label>
          {error && <div className="authenticator-error">{error}</div>}
          <div className="authenticator-sheet-actions">
            {editing !== "new" && <button className="danger" onClick={() => void remove(editing)}><Trash2 size={16} />Remove</button>}
            <button className="authenticator-save" disabled={busy} onClick={() => void submit()}>{busy ? "Saving…" : editing === "new" ? "Add account" : "Save changes"}</button>
          </div>
        </section>
      </div>}
      {exportOpen && <div className="authenticator-sheet-backdrop" onPointerDown={(event) => { if (event.target === event.currentTarget) closeExport(); }}>
        <section className="authenticator-sheet authenticator-export-sheet">
          <div className="authenticator-sheet-handle" />
          <header><div><strong>Export accounts</strong><small>Transfer to Google Authenticator or a compatible app</small></div><button aria-label="Close export" onClick={closeExport}><X size={20} /></button></header>
          {exportImages.length === 0 ? <>
            <div className="authenticator-export-warning"><ShieldAlert size={18} /><span><strong>Keep this QR private</strong><small>Anyone who scans it can generate your verification codes. It is created locally and is never uploaded.</small></span></div>
            <div className="authenticator-export-select-all"><strong>Select accounts</strong><button onClick={() => setExportSelected(new Set(records.filter((record) => !record.deletedAt && detailsFor(record).period === 30).map((record) => record.id)))}>Select all</button></div>
            <div className="authenticator-export-list">{records.filter((record) => !record.deletedAt).map((record) => {
              const details = detailsFor(record);
              const supported = details.period === 30;
              return <label className={!supported ? "not-transferable" : ""} key={record.id}><input type="checkbox" disabled={!supported} checked={exportSelected.has(record.id)} onChange={() => setExportSelected((current) => { const next = new Set(current); if (next.has(record.id)) next.delete(record.id); else next.add(record.id); return next; })} /><span className="authenticator-export-avatar">{details.issuer.slice(0, 1).toUpperCase()}</span><span><strong>{details.issuer}</strong><small>{supported ? details.account || "TOTP account" : "Non-standard timer cannot be transferred"}</small></span></label>;
            })}</div>
            {exportError && <div className="authenticator-error">{exportError}</div>}
            <button className="authenticator-export-create" disabled={exportBusy || exportSelected.size === 0} onClick={() => void createExport()}>{exportBusy ? "Creating QR…" : `Create transfer QR (${exportSelected.size})`}</button>
          </> : <div className="authenticator-export-qr">
            <div className="authenticator-export-warning"><ShieldAlert size={18} /><span><strong>Scan only on your new device</strong><small>In Google Authenticator, choose Transfer accounts → Import accounts.</small></span></div>
            <img src={exportImages[exportPage]} alt={`Authenticator transfer QR ${exportPage + 1} of ${exportImages.length}`} />
            <strong>Transfer QR {exportPage + 1} of {exportImages.length}</strong>
            <small>{exportImages.length > 1 ? "Scan every QR code in order." : "Your selected accounts are contained in this QR code."}</small>
            <div>{exportImages.length > 1 && <button disabled={exportPage === 0} onClick={() => setExportPage((page) => page - 1)}><ChevronLeft size={17} />Previous</button>}<button className="authenticator-export-done" onClick={closeExport}>Done</button>{exportImages.length > 1 && <button disabled={exportPage === exportImages.length - 1} onClick={() => setExportPage((page) => page + 1)}>Next<ChevronRight size={17} /></button>}</div>
          </div>}
        </section>
      </div>}
      {importOpen && <Suspense fallback={<div className="authenticator-sheet-backdrop"><div className="authenticator-import-loading">Opening scanner…</div></div>}><AuthenticatorImport
          existing={records.filter((record) => !record.deletedAt).map(detailsFor)}
          onClose={() => setImportOpen(false)}
          onImport={async (values) => { for (const value of values) await onCreate(value); setImportOpen(false); }}
        /></Suspense>}
    </div>
  </div>;
}
